import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Printer, Plus, Library, Wrench } from 'lucide-react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { TierLimitModal } from '@/components/TierLimitModal';
import { PrinterLimitFooter } from '@/components/PrinterLimitFooter';
import { SplitButton } from '@/components/SplitButton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs';
import { useToast } from '@/components/ToastProvider';
import { useTier } from '@/hooks/useAuth';
import {
  MyPrintersList,
  PrinterLibraryBrowser,
  PrinterForm,
  CustomPrinterLibraryForm,
} from '@/features/printers/components';
import { useTenantPrinters, useAddPrinterToLibrary, useCreatePrinterLibrary } from '@/features/printers/hooks';
import type { AddPrinterToLibraryInput, CreatePrinterLibraryInput } from '@maker-toolchest/shared';
import { getEffectivePrinterLimit } from '@maker-toolchest/shared';

export function PrintersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { tier } = useTier();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('my-printers');
  const [isCustomFormOpen, setIsCustomFormOpen] = useState(false);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [isTierLimitModalOpen, setIsTierLimitModalOpen] = useState(false);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(null);
  const [addPrinterError, setAddPrinterError] = useState<Error | null>(null);
  const [customPrinterError, setCustomPrinterError] = useState<Error | null>(null);

  const { data: printersData, isLoading } = useTenantPrinters({
    page: 1,
    limit: 100,
    sortBy: 'addedAt',
    sortOrder: 'desc'
  });
  const addToLibrary = useAddPrinterToLibrary();
  const createCustomPrinter = useCreatePrinterLibrary();

  const printers = printersData || [];
  const hasPrinters = printers.length > 0;

  // Get tier limits
  const printerLimit = tier ? getEffectivePrinterLimit(tier) : Infinity;
  const isAtPrinterLimit = printers.length >= printerLimit;

  // Set initial tab from URL search params
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'library') {
      setActiveTab('library');
    }
  }, [searchParams]);

  const handleSelectFromLibrary = (printerId: string) => {
    // Check tier limit before opening form
    if (isAtPrinterLimit) {
      setIsTierLimitModalOpen(true);
      return;
    }

    setSelectedPrinterId(printerId);
    setAddPrinterError(null); // Clear previous errors
    setIsAddFormOpen(true);
  };

  const handleAddPrinter = async (data: AddPrinterToLibraryInput) => {
    try {
      setAddPrinterError(null);
      await addToLibrary.mutateAsync(data);
      setIsAddFormOpen(false);
      setSelectedPrinterId(null);
      toast.success('Printer added successfully');
    } catch (error) {
      setAddPrinterError(error instanceof Error ? error : new Error('Failed to add printer'));
    }
  };

  const handleViewPrinter = (printer: any) => {
    navigate(`/printers/${printer.id}`);
  };

  const handleClonePrinter = (printer: any) => {
    // Clone by adding the same printer from library again
    setSelectedPrinterId(printer.printerId);
    setIsAddFormOpen(true);
  };

  const handleCreateCustomPrinter = async (data: CreatePrinterLibraryInput) => {
    try {
      setCustomPrinterError(null);
      const newPrinter = await createCustomPrinter.mutateAsync(data);
      setIsCustomFormOpen(false);
      // After creating custom printer, open the add form to create an instance
      setSelectedPrinterId(newPrinter.id);
      setAddPrinterError(null); // Clear any previous add errors
      setIsAddFormOpen(true);
      toast.success('Custom printer created successfully');
    } catch (error) {
      setCustomPrinterError(error instanceof Error ? error : new Error('Failed to create custom printer'));
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page header - Full width */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Printers</h1>
            <p className="text-sm text-foreground-muted">Manage your 3D printers</p>
          </div>
          <div className="flex items-center gap-2">
            <SplitButton
              label="Add Printer"
              icon={<Plus className="h-4 w-4" />}
              items={[
                {
                  label: 'From Library',
                  icon: <Library className="h-4 w-4" />,
                  onClick: () => setActiveTab('library'),
                },
                {
                  label: 'Custom Printer',
                  icon: <Wrench className="h-4 w-4" />,
                  onClick: () => {
                    if (isAtPrinterLimit) {
                      setIsTierLimitModalOpen(true);
                      return;
                    }
                    setIsCustomFormOpen(true);
                  },
                },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="my-printers">My Printers ({printers.length})</TabsTrigger>
            <TabsTrigger value="library">
              <Library className="mr-2 h-4 w-4" />
              Printer Library
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-printers" className="mt-4">
            {isLoading && !hasPrinters ? (
              <div className="rounded border border-border bg-background-subtle p-12 text-center">
                <p className="text-foreground-muted">Loading printers...</p>
              </div>
            ) : !hasPrinters ? (
              <div className="rounded border border-border bg-background-subtle p-4">
                <EmptyState
                  icon={<Printer className="h-10 w-10" />}
                  title="No printers yet"
                  description="Add your first 3D printer to start tracking prints and maintenance."
                  actionLabel="Browse Printer Library"
                  onAction={() => setActiveTab('library')}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <MyPrintersList
                  onViewPrinter={handleViewPrinter}
                  onClonePrinter={handleClonePrinter}
                />
                {isAtPrinterLimit && (
                  <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        You've reached your printer limit
                      </p>
                      <p className="mt-1 text-sm text-foreground-muted">
                        You have {printers.length} of {printerLimit} printers on the {tier} tier. Upgrade to add more printers.
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate('/settings/subscription')}
                    >
                      Upgrade
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="library" className="mt-4">
            <PrinterLibraryBrowser onPrinterSelect={handleSelectFromLibrary} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Printer limit footer */}
      <PrinterLimitFooter />

      {/* Add Printer Form */}
      <PrinterForm
        open={isAddFormOpen}
        onClose={() => {
          setIsAddFormOpen(false);
          setSelectedPrinterId(null);
          setAddPrinterError(null);
        }}
        onSubmit={handleAddPrinter}
        printerId={selectedPrinterId || undefined}
        loading={addToLibrary.isPending}
        error={addPrinterError}
      />

      {/* Custom Printer Library Form */}
      <CustomPrinterLibraryForm
        open={isCustomFormOpen}
        onClose={() => {
          setIsCustomFormOpen(false);
          setCustomPrinterError(null);
        }}
        onSubmit={handleCreateCustomPrinter}
        loading={createCustomPrinter.isPending}
        error={customPrinterError}
      />

      {/* Tier Limit Modal */}
      <TierLimitModal
        open={isTierLimitModalOpen}
        onClose={() => setIsTierLimitModalOpen(false)}
        resourceName="printers"
        resourceLabel="Printers"
        currentCount={printers.length}
        maxCount={printerLimit}
        tierName={tier || 'Free'}
        message={`You cannot add more printers on the ${tier || 'Free'} tier. To add more than ${printerLimit}, upgrade your plan.`}
      />
    </div>
  );
}
